import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import type { FieldDefinition, ReturnLossRow } from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";
import { ChartCard } from "@/components/charts/ChartCard";
import { RankedBarChart } from "@/components/charts/lazy";
import { LoadFailed, NoMatches } from "@/components/shell/EmptyState";
import { TableSkeleton } from "@/components/shell/LoadingState";
import { DataTable } from "@/components/table/DataTable";
import { Pagination } from "@/components/table/Pagination";
import { useCursorHistory } from "@/components/table/useCursorHistory";
import { StatusPill } from "@/modules/orders/returns/ReceiptView";
import { fetchAllPages } from "@/modules/reports/lib/pagination";
import { projectReturnLossRow } from "@/modules/reports/returns";
import { ReportHeader } from "@/modules/reports/ReportHeader";
import type { SectionScreenProps } from "@/modules/types";
import { formatCount } from "@/lib/chart-format";

/**
 * Damaged and missing returns (#529): every item a received parcel did not bring
 * back in good order, with when, which order, what, why and the photo.
 *
 * A list over the receipts' own rows, through the shared toolbar: filter by
 * outcome, search an order number, sort by date. A photo opens full size in a
 * new tab, through the same permission-checked route as every other attachment
 * on an order.
 */

function Photo({ row }: { row: ReturnLossRow }) {
  const data = useDataSource();
  if (row.photoId === null) {
    return <span className="text-muted-foreground">No photo</span>;
  }
  const url = data.returns.photoUrl(row.receiptId, row.photoId);
  if (!url) {
    return (
      <span className="text-muted-foreground">
        {row.photoCount} photo{row.photoCount === 1 ? "" : "s"}
      </span>
    );
  }
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-2"
      aria-label={`Photo of ${row.item} (${String(row.photoCount)})`}
    >
      <img
        src={url}
        alt={`${row.outcome === "damaged" ? "Damaged" : "Missing"} ${row.item}`}
        loading="lazy"
        className="size-12 rounded-md border object-cover"
      />
      {row.photoCount > 1 ? (
        <span className="text-muted-foreground text-xs">
          +{row.photoCount - 1}
        </span>
      ) : null}
    </a>
  );
}

function Outcome({ row }: { row: ReturnLossRow }) {
  return row.outcome === "damaged" ? (
    <StatusPill tone="bad">Damaged</StatusPill>
  ) : (
    <StatusPill tone="wait">Missing</StatusPill>
  );
}

/** A ranking longer than this is a list; the rows below have every reason. */
const REASONS_CHARTED = 6;

/**
 * Units lost, by the reason written on the receipt (#565): what is going
 * wrong most, before the rows that say which parcel.
 *
 * It has no table of its own behind it — the list under it is that table, row
 * by row, and filters the same way.
 */
function LossesByReason({ rows }: { rows: readonly ReturnLossRow[] }) {
  const byReason = new Map<string, { damaged: number; missing: number }>();
  for (const row of rows) {
    const reason = row.reason.trim() === "" ? "No reason given" : row.reason;
    const tally = byReason.get(reason) ?? { damaged: 0, missing: 0 };
    tally[row.outcome] += row.quantity;
    byReason.set(reason, tally);
  }
  const ranked = [...byReason.entries()]
    .map(([reason, tally]) => ({
      reason,
      ...tally,
      units: tally.damaged + tally.missing,
    }))
    .sort((a, b) => b.units - a.units);
  const shown = ranked.slice(0, REASONS_CHARTED);
  const units = ranked.reduce((sum, row) => sum + row.units, 0);

  return (
    <ChartCard
      title="Units lost by reason"
      description={`${formatCount(units)} ${units === 1 ? "unit" : "units"} came back damaged or did not come back, across ${formatCount(rows.length)} ${rows.length === 1 ? "row" : "rows"} matching the filter above.${
        ranked.length > shown.length
          ? ` The ${String(shown.length)} most common of ${String(ranked.length)} reasons are shown.`
          : ""
      } Every row is in the list below.`}
    >
      <RankedBarChart
        rows={shown.map((row) => ({
          key: row.reason,
          label: row.reason,
          value: row.units,
          note: `${formatCount(row.damaged)} damaged · ${formatCount(row.missing)} missing`,
        }))}
        valueLabel="Units"
        formatValue={formatCount}
        color="var(--series-8)"
        ariaLabel={`Units lost by reason: ${shown
          .map((row) => `${row.reason} ${String(row.units)}`)
          .join(", ")}.`}
      />
    </ChartCard>
  );
}

export function ReturnsReport({
  fields,
  filter,
  search,
  sort,
  onToggleSort,
  onClearFilter,
}: SectionScreenProps) {
  const data = useDataSource();
  const [limit, setLimit] = useState(25);
  const [history, setHistory] = useCursorHistory();
  const cursor = history[history.length - 1];

  const query = useQuery({
    queryKey: ["returns", "losses", filter, search, sort, limit, cursor],
    queryFn: () =>
      data.returns.listLosses({ filter, search, sort, limit, cursor }),
  });

  function renderOverride(field: FieldDefinition, row: ReturnLossRow) {
    switch (field.name) {
      case "outcome":
        return <Outcome row={row} />;
      case "photo":
        return <Photo row={row} />;
      default:
        return undefined;
    }
  }

  // Every matching row, not the page of them on screen: a chart of one page
  // would rank the reasons of twenty-five rows and call it the report.
  const allQuery = useQuery({
    queryKey: ["returns", "losses", "all", filter, search],
    queryFn: () =>
      fetchAllPages((page) => data.returns.listLosses(page), {
        filter,
        search,
      }),
  });

  if (query.isPending) return <TableSkeleton />;
  if (query.isError) {
    return (
      <LoadFailed
        message={query.error.message}
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="shrink-0 px-3 pt-4 sm:px-6 sm:pt-6">
        <ReportHeader
          readAt={query.dataUpdatedAt}
          className="border-b-0 pb-0"
        />
      </div>
      {allQuery.data && allQuery.data.length > 0 ? (
        <div className="shrink-0 border-b p-3 sm:p-6">
          <LossesByReason rows={allQuery.data} />
        </div>
      ) : null}
      <DataTable
        fields={fields}
        rows={query.data.items}
        getRowId={(row) => row.id}
        project={projectReturnLossRow}
        sort={sort}
        onToggleSort={onToggleSort}
        renderOverride={renderOverride}
        mobileRow={(row) => (
          <div className="flex min-w-0 items-start gap-3">
            <div className="min-w-0 flex-1 space-y-1">
              <div className="flex items-center justify-between gap-2">
                <span className="truncate text-base font-semibold">
                  {row.orderNumber}
                </span>
                <Outcome row={row} />
              </div>
              <p className="text-sm">
                {row.item} × {row.quantity}
              </p>
              <p className="text-muted-foreground text-sm">{row.reason}</p>
              <p className="text-muted-foreground text-xs">
                {row.receivedByName ?? "Someone"} ·{" "}
                {new Date(row.receivedAt).toLocaleDateString("en-IN", {
                  timeZone: "Asia/Kolkata",
                  day: "numeric",
                  month: "short",
                })}
              </p>
            </div>
            <Photo row={row} />
          </div>
        )}
        emptyState={<NoMatches onClear={onClearFilter} />}
        footer={
          <Pagination
            page={query.data}
            limit={limit}
            onLimitChange={(next) => {
              setLimit(next);
              setHistory([undefined]);
            }}
            history={history}
            onNavigate={setHistory}
          />
        }
      />
    </div>
  );
}
